import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "duration-histogram" }

import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js';
import type { RequestStats } from '@/esri/services/TempoDataService';

// Register Chart.js components
interface Props {
  stats: RequestStats[] | null;
  binCount?: number;
  title?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DurationHistogram',
  props: {
    stats: {},
    binCount: { default: 10 },
    title: { default: 'Request Duration Distribution' }
  },
  setup(__props: any) {

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const props = __props;

// Determine if we should use seconds (when most data is > 100ms)
const useSeconds = computed(() => {
  if (!props.stats || props.stats.length === 0) return false;
  const durations = props.stats.map(s => s.duration);
  const countOver100ms = durations.filter(d => d > 100).length;
  return countOver100ms > durations.length / 2; // More than half are over 100ms
});

// Get the time unit label
const timeUnit = computed(() => useSeconds.value ? 's' : 'ms');

// Calculate histogram bins from duration data
const histogramData = computed(() => {
  if (!props.stats || props.stats.length === 0) {
    return { labels: [], successCounts: [], errorCounts: [] };
  }

  const durations = props.stats.map(s => s.duration);
  const minDuration = Math.min(...durations);
  const maxDuration = Math.max(...durations);
  
  // Convert to seconds if needed
  const scale = useSeconds.value ? 1000 : 1;
  const scaledMin = minDuration / scale;
  const scaledMax = maxDuration / scale;
  
  // Handle edge case where all durations are the same
  if (minDuration === maxDuration) {
    const label = useSeconds.value 
      ? `${(minDuration / 1000).toFixed(2)}` 
      : `${Math.round(minDuration)}`;
    const successCount = props.stats.filter(s => s.status === 'success').length;
    const errorCount = props.stats.filter(s => s.status === 'error').length;
    return {
      labels: [label],
      successCounts: [successCount],
      errorCounts: [errorCount]
    };
  }

  // Create bins
  const binWidth = (scaledMax - scaledMin) / props.binCount;
  const labels: string[] = [];
  const successCounts: number[] = [];
  const errorCounts: number[] = [];

  // Format value based on scale
  const formatValue = (val: number) => {
    if (useSeconds.value) {
      // For seconds, use appropriate decimal places
      if (val < 0.1) return val.toFixed(3);
      if (val < 1) return val.toFixed(2);
      return val.toFixed(1);
    } else {
      // For milliseconds
      return Math.round(val).toString();
    }
  };

  for (let i = 0; i < props.binCount; i++) {
    const binStart = scaledMin + i * binWidth;
    const binEnd = scaledMin + (i + 1) * binWidth;
    
    labels.push(`${formatValue(binStart)}-${formatValue(binEnd)}`);
    
    // Count requests in this bin (compare in original ms units)
    const binStartMs = binStart * scale;
    const binEndMs = binEnd * scale;
    const binStats = props.stats.filter(s => {
      if (i === props.binCount - 1) {
        // Last bin includes the max value
        return s.duration >= binStartMs && s.duration <= binEndMs;
      }
      return s.duration >= binStartMs && s.duration < binEndMs;
    });
    
    successCounts.push(binStats.filter(s => s.status === 'success').length);
    errorCounts.push(binStats.filter(s => s.status === 'error').length);
  }

  return { labels, successCounts, errorCounts };
});

// Chart data configuration
const chartData = computed(() => ({
  labels: histogramData.value.labels,
  datasets: [
    {
      label: 'Successful Requests',
      backgroundColor: 'rgba(76, 175, 80, 0.7)',
      borderColor: 'rgb(76, 175, 80)',
      borderWidth: 1,
      data: histogramData.value.successCounts
    },
    {
      label: 'Failed Requests',
      backgroundColor: 'rgba(244, 67, 54, 0.7)',
      borderColor: 'rgb(244, 67, 54)',
      borderWidth: 1,
      data: histogramData.value.errorCounts
    }
  ]
}));

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: props.title
    },
    tooltip: {
      callbacks: {
        title: (context: { label: string }[]) => {
          return `Duration: ${context[0].label} ${timeUnit.value}`;
        }
      }
    },
    legend: {
      position: 'top' as const
    }
  },
  scales: {
    x: {
      title: {
        display: true,
        text: `Duration (${timeUnit.value})`
      },
      stacked: true
    },
    y: {
      title: {
        display: true,
        text: 'Number of Requests'
      },
      stacked: true,
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
}));

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (chartData.value.datasets.length > 0)
      ? (_openBlock(), _createBlock(_unref(Bar), {
          key: 0,
          data: chartData.value,
          options: chartOptions.value
        }, null, 8, ["data", "options"]))
      : (_openBlock(), _createBlock(_component_v_alert, {
          key: 1,
          type: "info",
          variant: "outlined",
          density: "compact"
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode(" No duration data available for histogram. ", -1)
          ]))]),
          _: 1
        }))
  ]))
}
}

})